import { type ComponentProps, type ReactNode } from 'react';

import type { SidebarGroupData } from '@/types';

import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';

interface CustomSidebarProps extends ComponentProps<typeof Sidebar> {
  header?: ReactNode;
  footer?: ReactNode;
  groups: SidebarGroupData[];
  activeId?: string;
  children?: ReactNode;
}

const CustomSidebar = ({
  header,
  footer,
  groups,
  activeId,
  children,
  ...props
}: CustomSidebarProps) => {
  return (
    <>
      <Sidebar {...props}>
        {header && <SidebarHeader>{header}</SidebarHeader>}
        <SidebarContent className="h-dvh">
          {groups.map((group) => (
            <SidebarGroup key={group.id}>
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => {
                    const { id, type, onClick } = item;

                    const isActive = id === activeId;
                    const linkProps = type === 'link' ? { href: item.href } : {};

                    return (
                      <SidebarMenuItem key={id}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          onClick={onClick}
                          className="data-[active=true]:bg-red-50 gap-3 data-[active=true]:text-primary! py-3.5 px-4 min-h-12"
                        >
                          <a {...linkProps} className="cursor-pointer">
                            {item.icon}
                            <span className="font-medium">{item.title}</span>
                          </a>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>
        {footer && <SidebarFooter>{footer}</SidebarFooter>}
      </Sidebar>
      <ScrollArea
        className="p-8 py-0 w-full border-l border-border bg-background"
        childrenClassName="pt-8"
      >
        {children}
      </ScrollArea>
    </>
  );
};

export default CustomSidebar;
